import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState(null); // { role_code, permissions, is_admin }

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try {
        const p = await api.get("/me/permissions");
        setPerms(p.data);
      } catch { setPerms({ is_admin: true, permissions: {} }); }
    } catch (_e) {
      setUser(false);
      setPerms(null);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    try {
      const p = await api.get("/me/permissions");
      setPerms(p.data);
    } catch { setPerms({ is_admin: true, permissions: {} }); }
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {}
    setUser(false);
    setPerms(null);
  };

  const can = useCallback((module, action = "view") => {
    if (!perms) return true; // still loading — permissive
    if (perms.is_admin) return true;
    const list = perms.permissions?.[module] || [];
    return list.includes(action);
  }, [perms]);

  return (
    <AuthContext.Provider value={{ user, perms, can, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
